import { upperFirst } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import {
  Controller,
  Control,
  UseFormWatch,
  UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IMissingInformation } from '../../../pages/ListingDetail/ListingDetail.types';
import {
  CONTRACT_TYPE,
  LANDLORD_REASON,
  PROPERTY_TYPE,
} from '../../../shared/constants';
import useStore from '../../../store';
import DropdownInput from '../../___design/DropDownInput';
import MultiSelectDropDownInput from '../../___design/MultiSelectDropDownInput';
import TextInput from '../../___design/TextInput';
import { YES_AND_NO_OPTIONS } from './constants/listingDetailForm.const';
import { StyledListingDetailFormBody } from './ListingDetailForm.styles';
import { IListingDetailForm } from './ListingDetailForm.types';
import {
  filterRequiredFieldForHouseInformation,
  getContractTypeOptions,
  getLandlordExpenseOptions,
  getRentExpenseOptions,
  getRentalAgreementOptions,
  getTenantExpenseOptions,
  getTypeOfTaxOptions,
} from './utils';

const ListingDetailForm = ({
  control,
  watch,
  setValue,
  setMissingHouseInformation,
}: {
  control: Control<IListingDetailForm>;
  watch: UseFormWatch<IListingDetailForm>;
  setValue: UseFormSetValue<IListingDetailForm>;
  setMissingHouseInformation: Dispatch<
    SetStateAction<IMissingInformation | null>
  >;
}): JSX.Element => {
  const user = useStore(state => state.user);
  const { t } = useTranslation(['listingDetail']);
  const values = watch();

  const rentExpenseOptions = useMemo(() => {
    return getRentExpenseOptions(values);
  }, [values?.rent_expense, values?.tenant_expense, values?.landlord_expense]);

  const tenantExpenseOptions = useMemo(() => {
    return getTenantExpenseOptions(values);
  }, [values?.rent_expense, values?.tenant_expense, values?.landlord_expense]);

  const landlordExpenseOptions = useMemo(() => {
    return getLandlordExpenseOptions(values);
  }, [values?.rent_expense, values?.tenant_expense, values?.landlord_expense]);

  const contractTypeOptions = useMemo(() => {
    return getContractTypeOptions(values, user);
  }, [
    values?.rental_agreement,
    values?.contract_tax_type_flat,
    values?.contract_type,
  ]);

  const rentalAgreementOptions = useMemo(() => {
    return getRentalAgreementOptions(values);
  }, [
    values?.rental_agreement,
    values?.contract_tax_type_flat,
    values?.contract_type,
  ]);

  const typeOfTaxOptions = useMemo(() => {
    return getTypeOfTaxOptions(values, setValue);
  }, [
    values?.rental_agreement,
    values?.contract_tax_type_flat,
    values?.contract_type,
  ]);

  const contractTypeSelect = {
    id: 'contract_type',
    name: 'contract_type',
    label: upperFirst(t('listingDetail:typeOfContract')),
    placeholder: upperFirst(t('listingDetail:typeOfContract')),
    options: contractTypeOptions,
    value: '',
  };

  const reasonForTemporarySelect = {
    id: 'contract_landlord_reason',
    name: 'contract_landlord_reason',
    label: upperFirst(t('listingDetail:reasonForTemporaryContract')),
    placeholder: upperFirst(t('listingDetail:reasonForTemporaryContract')),
    options: Object.values(LANDLORD_REASON),
    value: '',
  };

  const rentalAgreementSelect = {
    id: 'rental_agreement',
    name: 'rental_agreement',
    label: upperFirst(t('listingDetail:typeOfAgreement')),
    placeholder: upperFirst(t('listingDetail:typeOfAgreement')),
    options: rentalAgreementOptions,
    value: '',
  };

  const typeOfTaxSelect = {
    id: 'contract_tax_type_flat',
    name: 'contract_tax_type_flat',
    label: upperFirst(t('listingDetail:typeOfTax')),
    placeholder: upperFirst(t('listingDetail:typeOfTax')),
    options: typeOfTaxOptions,
    value: '',
  };

  const portionOfPropertySelect = {
    id: 'property_type',
    name: 'property_type',
    label: upperFirst(t('listingDetail:portionOfProperty')),
    placeholder: upperFirst(t('listingDetail:portionOfProperty')),
    options: Object.values(PROPERTY_TYPE),
    value: '',
  };

  const tenantExpenseSelect = {
    id: 'tenant_expense',
    name: 'tenant_expense',
    label: upperFirst(t('listingDetail:tenantWillChangeTheNameOn')),
    placeholder: upperFirst(t('listingDetail:tenantWillChangeTheNameOn')),
    options: tenantExpenseOptions,
  };

  const landlordExpenseSelect = {
    id: 'landlord_expense',
    name: 'landlord_expense',
    label: upperFirst(t('listingDetail:tenantWillPayTheLandord')),
    placeholder: upperFirst(t('listingDetail:tenantWillPayTheLandord')),
    options: landlordExpenseOptions,
  };

  const rentExpenseSelect = {
    id: 'rent_expense',
    name: 'rent_expense',
    label: upperFirst(t('listingDetail:expensesIncluded')),
    placeholder: upperFirst(t('listingDetail:expensesIncluded')),
    options: rentExpenseOptions,
  };

  const allowPetsSelect = {
    id: 'pets_allowed',
    name: 'pets_allowed',
    label: upperFirst(t('listingDetail:animals')),
    placeholder: upperFirst(t('listingDetail:animals')),
    options: YES_AND_NO_OPTIONS,
    value: '',
  };

  const allowStudentsSelect = {
    id: 'allow_students',
    name: 'allow_students',
    label: upperFirst(t('listingDetail:students')),
    placeholder: upperFirst(t('listingDetail:students')),
    options: YES_AND_NO_OPTIONS,
    value: '',
  };

  useEffect(() => {
    const missingValue = filterRequiredFieldForHouseInformation({ values });
    if (missingValue.fields.length === 0) {
      setMissingHouseInformation(null);
      return;
    }
    setMissingHouseInformation(missingValue);
  }, [JSON.stringify(values)]);

  useEffect(() => {
    if (values?.contract_type?.value !== CONTRACT_TYPE.TEMPORARY.value) {
      setValue('contract_landlord_reason', null);
    }
  }, [values?.contract_type]);

  return (
    <StyledListingDetailFormBody>
      <DropdownInput
        control={control}
        id={contractTypeSelect.id}
        name={contractTypeSelect.name}
        label={contractTypeSelect.label}
        placeholder={contractTypeSelect.placeholder}
        options={contractTypeSelect.options}
        helperText={
          values.contract_type?.value === CONTRACT_TYPE.TEMPORARY.value
            ? `Note: ${upperFirst(
                t('listingDetail:helperTextForContractTypeTemporary'),
              )}`
            : ''
        }
      />
      {values.contract_type?.value === CONTRACT_TYPE.TEMPORARY.value && (
        <DropdownInput
          control={control}
          id={reasonForTemporarySelect.id}
          name={reasonForTemporarySelect.name}
          label={reasonForTemporarySelect.label}
          placeholder={reasonForTemporarySelect.placeholder}
          options={reasonForTemporarySelect.options}
        />
      )}
      <DropdownInput
        control={control}
        id={rentalAgreementSelect.id}
        name={rentalAgreementSelect.name}
        label={rentalAgreementSelect.label}
        placeholder={rentalAgreementSelect.placeholder}
        options={rentalAgreementSelect.options}
      />
      <DropdownInput
        control={control}
        id={typeOfTaxSelect.id}
        name={typeOfTaxSelect.name}
        label={typeOfTaxSelect.label}
        placeholder={typeOfTaxSelect.placeholder}
        options={typeOfTaxSelect.options}
      />
      <DropdownInput
        control={control}
        id={portionOfPropertySelect.id}
        name={portionOfPropertySelect.name}
        label={portionOfPropertySelect.label}
        placeholder={portionOfPropertySelect.placeholder}
        options={portionOfPropertySelect.options}
      />
      <MultiSelectDropDownInput
        control={control}
        id={tenantExpenseSelect.id}
        name={tenantExpenseSelect.name}
        label={tenantExpenseSelect.label}
        placeholder={tenantExpenseSelect.placeholder}
        options={tenantExpenseSelect.options}
      />
      <MultiSelectDropDownInput
        control={control}
        id={landlordExpenseSelect.id}
        name={landlordExpenseSelect.name}
        label={landlordExpenseSelect.label}
        placeholder={landlordExpenseSelect.placeholder}
        options={landlordExpenseSelect.options}
      />
      <MultiSelectDropDownInput
        control={control}
        id={rentExpenseSelect.id}
        name={rentExpenseSelect.name}
        label={rentExpenseSelect.label}
        placeholder={rentExpenseSelect.placeholder}
        options={rentExpenseSelect.options}
      />
      <Controller
        control={control}
        name="max_number_of_guests"
        render={({ field: { onChange, value } }) => (
          <TextInput
            id="max_number_of_guests"
            label={upperFirst(t('listingDetail:maximumNumberOfTenants'))}
            name="max_number_of_guests"
            type="number"
            value={value ?? '0'}
            onChange={onChange}
            minValue={0}
          />
        )}
      />
      <DropdownInput
        control={control}
        id={allowPetsSelect.id}
        name={allowPetsSelect.name}
        label={allowPetsSelect.label}
        placeholder={allowPetsSelect.placeholder}
        options={allowPetsSelect.options}
      />
      <DropdownInput
        control={control}
        id={allowStudentsSelect.id}
        name={allowStudentsSelect.name}
        label={allowStudentsSelect.label}
        placeholder={allowStudentsSelect.placeholder}
        options={allowStudentsSelect.options}
      />
    </StyledListingDetailFormBody>
  );
};

export default ListingDetailForm;
